import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { parseAsArrayOf, parseAsString, parseAsStringEnum, useQueryState } from 'nuqs';
import { useState } from 'react';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { getBlogPostAsync } from '../../../../queries/Blog/useBlogUpdateScheduleMutation';
import { useInfiniteReportsBlogsQuery } from '../../../../queries/Reports/useInfiniteReportsBlogsQuery';
import { useStickyTableEdges } from '../../../hooks/useStickyTableEdges';
import { Waypoint } from 'react-waypoint';
import { Button } from '../../../components-v2/Button';
import { DataGrid, DataGridColumn, DataGridRow, DataGridRows } from '../../DataGrid';
import { styleSheet as dataGridStyles } from '../../DataGrid/styles';
import { LoadingSpinner } from '../../LoadingSpinner';
import { blogReportDateBounds } from './blogReportDateBounds';
import { allBlogStatusFilterValues } from './blogStatusFilters';
import { BlogReportingToolbar } from './BlogReportingToolbar';
import { BlogReportingTableRow } from './presentation';
import { styleSheet } from './styles';

const PAGE_SIZE = 25;

interface IProps {
	/** Admin has no per-post editor to open, so its titles are text rather than links. */
	canOpenPosts?: boolean;
	/** Take height from the host rather than the viewport, for a host that sizes its own content. */
	fillsContainer?: boolean;
	/** The account being viewed, where that is not the one the session is signed in as. */
	impersonationContext?: Api.IImpersonationContext;
}

export const BlogReportingTable = ({
	canOpenPosts = true,
	fillsContainer = false,
	impersonationContext: contextProp,
}: IProps = {}) => {
	const userSession = useUserSession();
	const impersonationContext = contextProp ?? userSession.impersonationContext;
	const fullscreenModal = useFullscreenModal();
	const errorMessages = useErrorMessages();
	const [openingId, setOpeningId] = useState<string | null>(null);
	const { isAtLeftOfTable, isAtTopOfTable, onScroll } = useStickyTableEdges();
	const [status, setStatus] = useQueryState(
		'status',
		parseAsArrayOf(parseAsStringEnum<Api.BlogStatus>(allBlogStatusFilterValues)).withDefault(allBlogStatusFilterValues)
	);
	const [sentBy, setSentBy] = useQueryState('sentBy', parseAsString);
	const [from, setFrom] = useQueryState('startDate', parseAsString.withDefault(''));
	const [to, setTo] = useQueryState('endDate', parseAsString.withDefault(''));

	const { endDate, startDate } = blogReportDateBounds(from, to);
	const blogReportsQuery = useInfiniteReportsBlogsQuery({
		endDate,
		impersonationContext,
		pageSize: PAGE_SIZE,
		// `sendFromUserId` is the hook's name for the request's `userId`; the URL calls it `sentBy`.
		sendFromUserId: sentBy ?? undefined,
		startDate,
		status,
	});

	const onStatusChanged = (value: Api.BlogStatus[]) => {
		setStatus(value.length === 0 ? allBlogStatusFilterValues : value);
	};
	const onDateRangeChanged = (nextFrom: string, nextTo: string) => {
		setFrom(nextFrom);
		setTo(nextTo);
	};
	const onClearFilters = () => {
		setStatus(allBlogStatusFilterValues);
		setSentBy(null);
		setFrom('');
		setTo('');
	};

	// The edit screen reads its post from router location state, never from the `:id` in the path, so the
	// full post has to be fetched here — a report view has no content and would blank the editor.
	const onTitleClick = async (report: Api.IBlogReportView) => {
		if (!report.id || openingId) {
			return;
		}
		setOpeningId(report.id);
		try {
			const post = await getBlogPostAsync({ id: report.id, impersonationContext, userSession });
			fullscreenModal?.history?.push({
				pathname: `/blog/post/edit/${post.id}`,
				state: { model: { post, impersonationContext } },
			});
		} catch (error) {
			errorMessages.pushApiError(error);
		} finally {
			setOpeningId(null);
		}
	};

	const reports = blogReportsQuery.data?.pages.flatMap(page => page.values ?? []) ?? [];
	return (
		<div className={css(fillsContainer ? styleSheet.fillHost : undefined)}>
			<BlogReportingToolbar
				from={from}
				impersonationContext={impersonationContext}
				onClearFilters={onClearFilters}
				onDateRangeChanged={onDateRangeChanged}
				onSentByChanged={setSentBy}
				onStatusChanged={onStatusChanged}
				sentBy={sentBy}
				status={status}
				to={to}
			/>
			{blogReportsQuery.status === 'success' && reports.length > 0 ? (
				<div className={css(styleSheet.tableWrapper, fillsContainer ? styleSheet.tableWrapperFill : undefined)}>
					<div
						className={css(styleSheet.tableWrapperInner, fillsContainer ? styleSheet.tableWrapperInnerFill : undefined)}
						onScroll={onScroll}
					>
						<DataGrid>
							{/* `DataGridColumns` would put the sticky class on the thead; the row is what has to stick. */}
							<thead>
								<DataGridRow styles={[dataGridStyles.headerRow, !isAtTopOfTable ? styleSheet.rowSticky : undefined]}>
									<DataGridColumn
										styles={[
											styleSheet.headerCell,
											styleSheet.titleColumn,
											!isAtTopOfTable || !isAtLeftOfTable ? styleSheet.headerCellSticky : undefined,
											!isAtLeftOfTable ? styleSheet.headerCellStickyRightScroll : undefined,
										]}
									>
										Blog Title
									</DataGridColumn>
									<DataGridColumn styles={[styleSheet.headerCell, styleSheet.statusColumn]}>Status</DataGridColumn>
									<DataGridColumn styles={[styleSheet.headerCell, styleSheet.dateColumn]}>Created On</DataGridColumn>
									<DataGridColumn styles={[styleSheet.headerCell, styleSheet.dateTimeColumn]}>
										Published On
									</DataGridColumn>
									<DataGridColumn styles={[styleSheet.headerCell, styleSheet.authorColumn]}>Author</DataGridColumn>
								</DataGridRow>
							</thead>
							<DataGridRows>
								{reports.map(report => (
									<BlogReportingTableRow
										isAtLeftOfTable={isAtLeftOfTable}
										isOpening={openingId === report.id}
										key={report.id}
										onTitleClick={canOpenPosts ? onTitleClick : undefined}
										report={report}
									/>
								))}
							</DataGridRows>
						</DataGrid>
						{blogReportsQuery.isFetchingNextPage ? (
							<LoadingSpinner className={css(styleSheet.loading)} type='large' />
						) : null}
						{/* Inside the scroller on purpose: react-waypoint binds to its nearest scrollable ancestor, and from
						    outside it would bind to a window that never scrolls and page through everything at once. */}
						{blogReportsQuery.hasNextPage && !blogReportsQuery.isFetchingNextPage && !blogReportsQuery.isError ? (
							<Waypoint bottomOffset='-200px' onEnter={() => blogReportsQuery.fetchNextPage()} />
						) : null}
					</div>
				</div>
			) : null}
			{blogReportsQuery.isLoading ? <LoadingSpinner className={css(styleSheet.loading)} type='large' /> : null}
			{blogReportsQuery.status === 'success' && reports.length === 0 ? (
				<div className={css(styleSheet.noneFound)}>No Blog Posts Found</div>
			) : null}
			{/* A single malformed row makes the API fail the whole page, so a failure has to be visible here and
			    not only as a toast — otherwise the table just stops with no explanation. */}
			{blogReportsQuery.isError ? (
				<div className={css(styleSheet.loadFailed)}>
					<div>Something went wrong loading these blog posts.</div>
					<Button onClick={() => blogReportsQuery.refetch()} variant='secondary'>
						Try again
					</Button>
				</div>
			) : null}
		</div>
	);
};
